/**
 * OCC filings grouped by footprint for the deals map. Pure.
 *
 * Pooling, spacing, increased-density and location-exception filings very
 * often cover the exact same unit. Drawing each as its own outline stacks
 * identical lines; instead every distinct geometry ("footprint") is drawn
 * once, with letter badges for the kinds filed on it, and the hover lists
 * every filing there, newest first.
 *
 * WINDOW. The control is labelled "OCC filings, last 12 months" but
 * GET /api/spatial/occ/units applies no date filter, so the window is applied
 * here: a filing is kept when the latest of its app / order / latest-hearing
 * dates is within `windowDays` (default 365) of `now`.
 */
import { parseDateMs, formatDateOnly } from "./dateFormat";
import { titleCase } from "./metadataV2Columns";
import { OCC_KINDS, occKindLabel } from "./occUnits";

/**
 * Badge letters per kind. KNOWN GAP: source literals, like the kind labels
 * in occUnits.js — there is no data source for kind display values.
 */
export const OCC_KIND_LETTERS = {
  pooling: "P",
  spacing: "S",
  increased_density: "ID",
  location_exception: "LE",
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Latest of a filing's app / order / latest-hearing dates (epoch ms or null). */
export const filingDateMs = (props) => {
  let best = null;
  for (const v of [props?.app_date, props?.order_date, props?.latest_hearing_date]) {
    const ms = parseDateMs(v);
    if (ms != null && (best == null || ms > best)) best = ms;
  }
  return best;
};

/** A filing that has gone to order (app_or_order "ORDER"), vs an application. */
export const isOrderFiling = (props) => /order/i.test(String(props?.app_or_order ?? ""));

const round = (v) => Math.round(v * 1e6) / 1e6;
const roundCoords = (c) => (Array.isArray(c) ? c.map(roundCoords) : round(c));

/** Identity of a geometry: its coordinates rounded to ~10 cm. */
export const footprintKey = (geometry) =>
  geometry ? `${geometry.type}:${JSON.stringify(roundCoords(geometry.coordinates))}` : null;

/**
 * @param {Array<object>} features  OCC unit features (any kinds)
 * @param {{ kinds?: string[], now?: number, windowDays?: number }} [options]
 *   kinds: the kinds switched on (default all)
 * @returns {{ fc: object, filingsById: Map<string, object[]> }}
 *   fc: one feature per footprint, props { footprint_id, primary_kind (kind
 *   of the newest filing), badge ("P\nS" — kinds present, display order),
 *   filing_count }; filingsById: footprint_id -> filing props, newest first
 */
export const groupOccFootprints = (features, options = {}) => {
  const kinds = new Set(options.kinds ?? OCC_KINDS);
  const now = options.now ?? Date.now();
  const cutoff = now - (options.windowDays ?? 365) * MS_PER_DAY;

  const groups = new Map();
  for (const f of Array.isArray(features) ? features : []) {
    const p = f?.properties || {};
    if (!kinds.has(p.kind)) continue;
    const when = filingDateMs(p);
    if (when == null || when < cutoff) continue;
    const key = footprintKey(f.geometry);
    if (!key) continue;
    let g = groups.get(key);
    if (!g) {
      g = { geometry: f.geometry, filings: [] };
      groups.set(key, g);
    }
    g.filings.push({ ...p, _when: when });
  }

  const fc = { type: "FeatureCollection", features: [] };
  const filingsById = new Map();
  let n = 0;
  for (const g of groups.values()) {
    g.filings.sort((a, b) => b._when - a._when);
    const id = `fp${n++}`;
    const present = new Set(g.filings.map((x) => x.kind));
    fc.features.push({
      type: "Feature",
      geometry: g.geometry,
      properties: {
        footprint_id: id,
        primary_kind: g.filings[0].kind,
        badge: OCC_KINDS.filter((k) => present.has(k))
          .map((k) => OCC_KIND_LETTERS[k])
          .join("\n"),
        filing_count: g.filings.length,
      },
    });
    filingsById.set(
      id,
      g.filings.map(({ _when, ...rest }) => rest) // eslint-disable-line no-unused-vars
    );
  }
  return { fc, filingsById };
};

/**
 * The ~6 lines shown per filing in the hover. Labels from `occ_units`
 * metadata (title-cased field name when a row is missing).
 *
 *   OCC type · order type | cause # (order # once an order issued) |
 *   status | applicant | formation | one date: order date for an order,
 *   else the latest hearing (app date when there is none)
 *
 * @returns {Array<{field:string,label:string,text:string}>}
 */
export const occFilingRows = (filing, metadata) => {
  const p = filing || {};
  const meta = Array.isArray(metadata) ? metadata : [];
  const label = (field) => meta.find((m) => m?.field === field)?.label || titleCase(field);
  const rows = [];
  const push = (field, text) => {
    if (text === null || text === undefined || text === "") return;
    rows.push({ field, label: label(field), text: String(text) });
  };
  const order = isOrderFiling(p);

  push("kind", [occKindLabel(p.kind), p.order_type].filter(Boolean).join(" · "));
  if (order && p.order_number) push("order_number", p.order_number);
  else push("cause_number", p.cause_number);
  push("status", p.status);
  push("applicant", p.applicant);
  push("formation", p.formation);
  if (order && p.order_date) push("order_date", formatDateOnly(p.order_date));
  else if (p.latest_hearing_date) push("latest_hearing_date", formatDateOnly(p.latest_hearing_date));
  else if (p.app_date) push("app_date", formatDateOnly(p.app_date));
  return rows;
};
